import { CustomerType, InterviewService } from '@bequeathed/ngx-services'

/**
 * Analytics helpers for the interview app's funnel instrumentation (task 8.2).
 *
 * The interview funnel stages (`will_interview_launched`, `will_interview_page`,
 * `will_interview_complete`, `advice_complete`, `interview_process_complete`) are
 * shared across all three customer journeys (CHARITY / LAWFIRM / REGULAR); the `customer_type`
 * dimension is what distinguishes them (design §5). This util centralises the
 * single way the interview app derives that dimension so every stage stamps a
 * consistent value, mirroring the `resolveCustomerType()` convention the home
 * app uses for its registration/landing events.
 *
 * @author Anonymous Funnel Analytics
 */
export class AnalyticsUtils {

    /**
     * Derives the analytics `customer_type` for the current interview from the
     * will attributes the {@link InterviewService} resolves on start/load.
     *
     * The interview service records the originating charity / lawfirm against the
     * will (`willAttributes`), and also captures initiator params from the route.
     * A charity initiator wins (CHARITY journey), then a lawfirm initiator
     * (LAWFIRM journey); any other interview is the generic REGULAR journey.
     *
     * @param interviewService the interview service holding the resolved will attributes
     * @returns the {@link CustomerType} for the current interview's funnel events
     */
    public static resolveCustomerType(interviewService: InterviewService): CustomerType {
        const charity = interviewService?.willAttributes?.get('charity')
            || interviewService?.initiatorParams?.['beq_initiator_type'] === 'charity'
        const lawfirm = interviewService?.willAttributes?.get('lawfirm')
            || interviewService?.initiatorParams?.['lawfirm']
            || interviewService?.initiatorParams?.['beq_initiator_type'] === 'lawfirm'

        if (charity) {
            return CustomerType.CHARITY
        }
        if (lawfirm) {
            return CustomerType.LAWFIRM
        }
        return CustomerType.REGULAR
    }
}
